import axios from "axios";
import { FHIR_METHODS } from "config/constants/adapter_constants";
import { getTenantFromToken } from "config/helpers/common_helper";
import { handleLogout, TokenValidate } from "config/helpers/logout_helper";
import { auth } from "firebase-setup";
import jwtDecode from "jwt-decode";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  change_app_language,
  change_login,
  reset_state,
} from "redux/actions/AppActions";
import { set_is_logging_out } from "redux/actions/AuthActions";
import { reset_subscription } from "redux/actions/SubscriptionAction";
import { constants } from "redux/constants/redux-constants";
import { store } from "redux/store";

const generateInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(`jwtToken`);
      if (token) {
        const jwtPayload = jwtDecode(token);
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["x-enliv-tenant-id"] = jwtPayload?.firebase?.tenant;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  AxiosInstance.interceptors.response.use(
    (response) => {
      TokenValidate();
      return response;
    },

    async (error) => {
      const originalRequest = error?.config;
      console.log("originalRequest._retry", originalRequest._retry);
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        // if (error?.response?.data?.message === "Unauthorized") {
        //   isTokenToast && toast.error("Your session has been expired, please login again.");
        //   isTokenToast = false;
        //   handleLogout()
        //   return Promise.resolve();
        // }
        try {
          const user = auth?.currentUser;
          if (user) {
            const token = await user.getIdToken(true);
            localStorage.setItem("jwtToken", token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return AxiosInstance(originalRequest);
          }
        } catch (err) {
          if (err?.code === "auth/user-token-expired") {
            handleLogout();
            return Promise.resolve();
            // toast.error("Your session has been expired, please login again.")
            // store.dispatch(set_is_logging_out(true))
            // const { app } = store.getState()
            // setTimeout(() => {
            //   const language = app?.language;
            //   store.dispatch(change_login(constants?.USER_LOGOUT))
            //   store.dispatch(reset_state())
            //   store.dispatch(change_app_language(language || "en"))
            //   store.dispatch(reset_subscription())
            //   store.dispatch(set_is_logging_out(false))
            // }, 1000);
          }
          console.log(err);
        }
      }
      return Promise.reject(error);
    }
  );
};

const generateMetaInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(`jwtToken`);
      if (token) {
        const jwtPayload = jwtDecode(token);
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["x-enliv-tenant-id"] = jwtPayload?.firebase?.tenant ;
        config.headers["x-hasura-role"] = `admin`;
        config.headers[
          "x-hasura-admin-secret"
        ] = `c042293c-7ae1-43c5-bda1-fc3519f5fac2`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

const generateBridgeRequestInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(async (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });
};

let HTTPRequest = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

generateInterceptors(HTTPRequest);

export const HTTPAidaBaseService = axios.create({
  baseURL: process.env.REACT_APP_AIDA_SERVICE_URL,
});

generateInterceptors(HTTPAidaBaseService);

let HTTPRequestUnauthenticated = axios.create({
  // Must be REACT_APP_API_URL, not REACT_APP_AUTH_URL: prod's load balancer
  // only proxies paths under /api/* to any real backend (path-matcher-8),
  // and REACT_APP_API_URL is the one configured as the same-origin "/api"
  // base that actually routes through it. REACT_APP_AUTH_URL is a separate,
  // directly-callable auth-service URL used elsewhere; it isn't routed
  // through the prod load balancer's /api/auth/* rule, so using it here
  // makes every call miss all four routing rules and fall through to the
  // static-hosting SPA fallback (index.html) instead of the real backend -
  // this exact regression was already fixed once (see "QA High Priority
  // Issues fixes") before being silently reintroduced by a later merge of a
  // long-diverged feature branch that predated that fix.
  baseURL: process.env.REACT_APP_API_URL,
  headers: { "ngrok-skip-browser-warning": "true" },
});

export const HTTPBridgeService = axios.create({
  //temproray base Url till Proxy BE gets deployed
  baseURL: process.env.REACT_APP_EHR_PROXY_URL,
  headers: {
    "x-api-key": process.env.REACT_APP_EHR_PROXY_API_KEY,
    "Content-Type": "application/json",
  },
});

export const HTTPLocalService = axios.create({
  baseURL: "http://localhost:8000",
  headers: {
    "x-api-key": process.env.REACT_APP_EHR_PROXY_API_KEY,
    "Content-Type": "application/json",
  },
});

generateBridgeRequestInterceptors(HTTPLocalService);
generateBridgeRequestInterceptors(HTTPBridgeService);

export const HTTPVimService = axios.create({
  baseURL: "https://vim-dev-service-152128589088.us-east4.run.app",
  headers: {
    "Content-Type": "application/json",
  },
});

export const HTTPVimLocalService = axios.create({
  baseURL: "http://localhost:12000",
  headers: {
    "Content-Type": "application/json",
  },
});

generateBridgeRequestInterceptors(HTTPVimService);
generateBridgeRequestInterceptors(HTTPVimLocalService);

// These three use HTTPAidaBaseService (REACT_APP_AIDA_SERVICE_URL), not
// HTTPRequest (REACT_APP_API_URL) - in production the latter is "/api" with
// no "/v1", and these relative paths never added it themselves, so every
// FHIR call landed on "/api/fhir/..." instead of "/api/ai/v1/fhir/...".
// That's a different, unversioned path that happens to still return FHIR
// data (likely proxied straight through elsewhere), which silently bypassed
// this service's own enterprise access-control logic entirely. Locally both
// env vars already point at the same URL, so this is a no-op there.
const FHIRSearch = (module, params, options) => {
  return HTTPAidaBaseService.get(
    `fhir/tenants/${getTenantFromToken()}/search/${module}${
      params ? "?" + params : ""
    }`, options
  );
};

const FHIRSave = (action, data) => {
  if (action === "executeBundle") {
    return HTTPAidaBaseService?.post(
      `fhir/tenants/${getTenantFromToken()}/` + "executeBundle",
      data
    );
  }
  let methodIdx = FHIR_METHODS.findIndex((f) => f.TYPE === action);
  if (methodIdx < 0) {
    console.error(
      `API Call Failed --> Please check if ${action} is a valid action.`
    );
    return false;
  }
  return HTTPAidaBaseService?.[_.lowerCase(action)](
    `fhir/tenants/${getTenantFromToken()}/` + FHIR_METHODS[methodIdx].RESOURCE,
    data
  );
};

const FHIRDelete = (resourceType, resourceId) => {
  return HTTPAidaBaseService?.delete(
    `fhir/tenants/${getTenantFromToken()}/deleteResource?resourceType=${resourceType}&resourceId=${resourceId}`
  );
};

const NokiService = (action, url = "", data) => {
  return HTTPAidaBaseService?.[_.lowerCase(action)](url, data);
};

const HTTPMetadata = (action, module, params = ``, payload = {}, url = "") => {
  const token = localStorage.getItem(`jwtToken`);
  let jwtPayload = {};
  if (token) jwtPayload = jwtDecode(token);
  let apiURL = process.env.REACT_APP_API_URL;
  let MetaHTTPRequest = axios.create({
    baseURL: apiURL,
  });
  generateMetaInterceptors(MetaHTTPRequest);
  const prepareUrl = url
    ? url + `/tenant/${getTenantFromToken()}`
    : `metadata/v1/${jwtPayload?.firebase?.tenant}/${module}${
        params ? "?" + params : ""
      }`;
  return MetaHTTPRequest?.[_.lowerCase(action)](prepareUrl, payload);
};

export default HTTPRequest;
export {
  FHIRSearch,
  FHIRSave,
  FHIRDelete,
  NokiService,
  HTTPMetadata,
  HTTPRequestUnauthenticated,
};
